import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaUsers, FaCreditCard, FaTags } from "react-icons/fa";
import CustomerHeader from "./CustomerHeader";
import CustomerFooter from "./CustomerFooter";
import AddressAutocompleteInput from "./AddressAutocompleteInput";
import "./CompleteOwnerRegistration.css";
import "../pages/auth/LoginStyles.css";
import "../pages/auth/PremiumAuth.css";
import { TIMEZONE_OPTIONS } from "../constants/timezones";

const CompleteOwnerRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  // A code typed on the open site (house promo, or an agent's code). Hidden when the visitor arrived
  // through an agent's share link — the link already carries the code, and asking twice invites a clash.
  const [typedCode, setTypedCode] = useState("");
  const [arrivedByLink] = useState(() => {
    try {
      // THIS VISIT only. A code left in localStorage by an earlier test must not hide the field.
      return sessionStorage.getItem("ogestyle_referral_from_link") === "1";
    } catch (_err) {
      return false;
    }
  });
  // A code already captured earlier in the same signup, so this screen does not ask twice.
  const [storedCode] = useState(() => {
    try {
      return localStorage.getItem("ogestyle_referral_code") || "";
    } catch (_err) {
      return "";
    }
  });

  // Fetch business categories from API, fallback to constants
  const [businessTypes, setBusinessTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Account Info
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    timezone: "",

    // Step 2: Business Info
    businessName: "",
    businessType: [],

    // Step 3: Business Address
    street: "",
    city: "",
    state: "",
    zip: "",
    locationMode: "",
    lat: '',
    lng: '',

    // Step 4: Business Contact Details
    businessPhone: "",
    businessEmail: "",
    website: "",
    facebook: "",
    instagram: "",
    twitter: "",
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  // The owner's timezone is deliberately NOT detected or pre-filled.
  //
  // Pre-selecting a value - even a correct detection - lets them submit without
  // ever making the choice, and hides a WRONG detection (travel, VPN, mismatched
  // locale) behind a value that looks plausible. The field is required and must be
  // actively chosen (policy 2026-10-03).

  // Fetch business categories from API on component mount
  useEffect(() => {
    const fetchBusinessCategories = async () => {
      try {
        const baseUrl =
          process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
        const response = await fetch(
          `${baseUrl}/api/platform-settings/business-categories`,
        );
        const data = await response.json();

        if (data.types && data.types.length > 0) {
          setBusinessTypes(data.types);
        } else {
          setBusinessTypes([]);
        }
      } catch (error) {
        setBusinessTypes([]);
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchBusinessCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleBusinessTypeChange = (type) => {
    const currentTypes = formData.businessType;
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];
    setFormData({ ...formData, businessType: newTypes });
    if (errors.businessType) {
      setErrors({ ...errors, businessType: "" });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.firstName.trim())
        newErrors.firstName = "First name is required";
      if (!formData.lastName.trim())
        newErrors.lastName = "Last name is required";
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Invalid email address";
      }
      if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
      if (!formData.timezone)
        newErrors.timezone = "Please choose your timezone";
      if (!smsConsent) {
        newErrors.smsConsent = "Please agree to receive SMS notifications";
      }
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      } else if (
        !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(formData.password)
      ) {
        newErrors.password =
          "Password must include uppercase, lowercase, number, and special character";
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    if (step === 2) {
      if (!formData.businessName.trim())
        newErrors.businessName = "Business name is required";
      if (formData.businessType.length === 0)
        newErrors.businessType = "Select at least one business type";
    }

    if (step === 3) {
      if (!formData.street.trim())
        newErrors.street = "Street address is required";
      if (!formData.city.trim()) newErrors.city = "City is required";
      if (!formData.state.trim()) newErrors.state = "State is required";
      if (!formData.zip.trim()) newErrors.zip = "ZIP code is required";
      if (!formData.locationMode.trim()) {
        newErrors.locationMode = "Select a location mode";
      }
      if (!formData.lat || !formData.lng) newErrors.lat = "Please select a valid address from the suggestions to ensure coordinates are captured.";
    }

    if (step === 4) {
      // Business contact details are optional — the personal ones are used when none is given.
      if (
        formData.businessEmail.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.businessEmail)
      ) {
        newErrors.businessEmail = "Invalid business email address";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    // Only allow submission when on step 4 (final step)
    if (currentStep !== 4) {
      return;
    }

    // Never submit without an actively chosen timezone (browser guard, not backend).
    if (!formData.timezone || !formData.timezone.trim()) {
      setErrors({ submit: "Please choose your timezone." });
      return;
    }

    if (!validateStep(4)) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
      // Arrived by an agent's link -> that code wins and the form never asks (one code, no collision).
      // Arrived on the open site -> they may type a house promo or an agent's code instead.
      let linkCode = null;
      try {
        linkCode = localStorage.getItem("ogestyle_referral_code");
      } catch (_err) {
        linkCode = null;
      }
      const referralCode = linkCode
        ? String(linkCode).trim().toUpperCase()
        : typedCode.trim()
          ? typedCode.trim().toUpperCase()
          : null;
      // Remembered so the pricing page can apply the same code's discount at checkout.
      if (referralCode) {
        try {
          localStorage.setItem("ogestyle_referral_code", referralCode);
        } catch (_err) {
          /* storage disabled: the code is still sent with the registration */
        }
      }
      const response = await fetch(`${baseUrl}/api/register/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // User info
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim(),
          password: formData.password,
          timezone: formData.timezone,
          smsConsent: smsConsent,
          // Business info
          businessName: formData.businessName.trim(),
          businessType: formData.businessType,
          street: formData.street.trim(),
          city: formData.city.trim(),
          state: formData.state.trim(),
          zipCode: formData.zip.trim(),
          locationMode: formData.locationMode,
          lat: formData.lat,
          lng: formData.lng,
          businessPhone: formData.businessPhone.trim(),
          businessEmail: formData.businessEmail.trim().toLowerCase(),
          website: formData.website.trim(),
          ...(referralCode ? { referralCode } : {}),
          socialMedia: {
            facebook: formData.facebook.trim(),
            instagram: formData.instagram.trim(),
            twitter: formData.twitter.trim(),
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific field errors
        if (data.field) {
          setErrors({ [data.field]: data.message });
          // Navigate to the step with the error
          if (
            data.field === "email" ||
            data.field === "password" ||
            data.field === "user"
          ) {
            setCurrentStep(1);
          } else if (
            data.field === "business" ||
            data.field === "businessName"
          ) {
            setCurrentStep(2);
          }
        } else {
          setErrors({
            submit: data.message || "Registration failed. Please try again.",
          });
        }
        return;
      }

      // Success!
      setSuccessMessage(data.message);

      // The signup is done, so the code has served its purpose: hand it to the pricing page and
      // clear the arrival markers, otherwise every later signup on this browser thinks it came from
      // a link and never offers the code field.
      try {
        if (referralCode) localStorage.setItem("ogestyle_promo_code", referralCode);
        localStorage.removeItem("ogestyle_referral_code");
        sessionStorage.removeItem("ogestyle_referral_from_link");
      } catch (_err) {
        /* storage disabled */
      }

      // Redirect to registration success page
      navigate(
        `/registration-success?email=${encodeURIComponent(formData.email)}`,
        {
          state: {
            email: formData.email,
          },
        },
      );
    } catch (error) {
      setErrors({
        submit: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSteps = 4;
  const progressPercentage = (currentStep / totalSteps) * 100;

  if (successMessage) {
    return (
      <>
        <CustomerHeader />
        <div className="registration-container">
          <div className="registration-card success-card">
            <div className="success-icon">✓</div>
            <h2>Registration Successful!</h2>
            <p>{successMessage}</p>
            <p className="success-subtitle">Redirecting to login...</p>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  return (
    <>
      <CustomerHeader />
      <div className="login-page og-premium-auth">
        {/* Left Panel - Branding */}
        <div className="login-branding">
          <div className="og-branding">
            <p className="og-eyebrow">For salon &amp; studio owners</p>
            <h2 className="og-brand-title">Grow your beauty business</h2>
            <p className="og-brand-sub">
              Run one salon or many — locations, your team, bookings and
              payments in one calm, connected place.
            </p>
            <div className="og-features">
              <div className="og-feature">
                <span className="og-feature-ic"><FaUsers /></span>
                <span>Manage staff &amp; bookings effortlessly</span>
              </div>
              <div className="og-feature">
                <span className="og-feature-ic"><FaCreditCard /></span>
                <span>Secure payments &amp; automatic payouts</span>
              </div>
              <div className="og-feature">
                <span className="og-feature-ic"><FaTags /></span>
                <span>Sell products &amp; manage inventory online</span>
              </div>
            </div>
            <div className="og-trust">
              <span>Every professional included</span>
              <span>No contract — cancel anytime</span>
              <span>No hidden fees</span>
            </div>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="login-form-panel">
          <div className="registration-container">
            <div className="registration-card">
              <div className="registration-header">
                <h1>Business Owner Registration</h1>
                <p className="registration-subtitle">
                  {currentStep === 1 &&
                    "Let's start with your account information"}
                  {currentStep === 2 && "Tell us about your business"}
                  {currentStep === 3 && "Where is your business located?"}
                  {currentStep === 4 && "Business contact details"}
                </p>
              </div>

              {/* Progress Bar */}
              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <div className="progress-steps">
                  {[1, 2, 3, 4].map((step) => (
                    <div
                      key={step}
                      className={`progress-step ${step <= currentStep ? "active" : ""} ${step < currentStep ? "completed" : ""}`}
                    >
                      <div className="step-circle">
                        {step < currentStep ? "✓" : step}
                      </div>
                      <div className="step-label">
                        {step === 1 && "Account"}
                        {step === 2 && "Business"}
                        {step === 3 && "Address"}
                        {step === 4 && "Contact"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  // Do nothing - form should only submit via explicit button click
                }}
                onKeyDown={(e) => {
                  // Prevent Enter key from submitting form - user must click buttons
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              >
                {/* Step 1: Account Information */}
                {currentStep === 1 && (
                  <div className="form-step">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="firstName">First Name *</label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className={errors.firstName ? "error" : ""}
                          placeholder="John"
                        />
                        {errors.firstName && (
                          <span className="error-message">
                            {errors.firstName}
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="lastName">Last Name *</label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className={errors.lastName ? "error" : ""}
                          placeholder="Doe"
                        />
                        {errors.lastName && (
                          <span className="error-message">
                            {errors.lastName}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Personal Email (Login) *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={errors.email ? "error" : ""}
                        placeholder="john@example.com"
                      />
                      {errors.email && (
                        <span className="error-message">{errors.email}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">Personal Phone (Account Security) *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={errors.phone ? "error" : ""}
                        placeholder="(555) 123-4567"
                      />
                      {errors.phone && (
                        <span className="error-message">{errors.phone}</span>
                      )}
                    </div>

                    {/* SMS Consent Checkbox (TCPA Compliance) */}
                    <div
                      style={{
                        marginTop: "24px",
                        padding: "20px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "12px",
                        border: "1px solid #e9ecef",
                      }}
                    >
                      <label
                        htmlFor="smsConsent"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "12px",
                          cursor: "pointer",
                          fontSize: "14px",
                          lineHeight: "1.6",
                          color: "#1f2937",
                        }}
                      >
                        <input
                          type="checkbox"
                          id="smsConsent"
                          checked={smsConsent}
                          onChange={(e) => setSmsConsent(e.target.checked)}
                          required
                          style={{
                            marginTop: "3px",
                            width: "18px",
                            height: "18px",
                            flexShrink: 0,
                            cursor: "pointer",
                            accentColor: "#1a659e",
                          }}
                        />
                        <span>
                          I agree to receive SMS notifications from OGESTYLE
                          about my business appointments, including booking
                          confirmations, reminders, and updates. Message
                          frequency may vary. Message and data rates may apply.
                          Text HELP for support. Reply STOP to opt-out anytime.{" "}
                          <a
                            href="/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#1a659e",
                              textDecoration: "underline",
                            }}
                          >
                            Privacy Policy
                          </a>
                        </span>
                      </label>
                      {errors.smsConsent && (
                        <span
                          className="error-message"
                          style={{ marginTop: "8px", display: "block" }}
                        >
                          {errors.smsConsent}
                        </span>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <input
                          type="password"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className={errors.password ? "error" : ""}
                          placeholder="••••••••"
                        />
                        {errors.password && (
                          <span className="error-message">
                            {errors.password}
                          </span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="confirmPassword">
                          Confirm Password *
                        </label>
                        <input
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className={errors.confirmPassword ? "error" : ""}
                          placeholder="••••••••"
                        />
                        {errors.confirmPassword && (
                          <span className="error-message">
                            {errors.confirmPassword}
                          </span>
                        )}
                      </div>
                    </div>
                    <small
                      className="field-hint"
                      style={{
                        marginTop: "-12px",
                        display: "block",
                        marginBottom: "20px",
                      }}
                    >
                      Min. 8 characters with uppercase, lowercase, number &
                      special character
                    </small>

                    <div className="form-group">
                      <label htmlFor="timezone">Your Timezone *</label>
                      <select
                        id="timezone"
                        name="timezone"
                        value={formData.timezone}
                        onChange={handleInputChange}
                        required
                        className="form-select"
                      >
                        <option value="">Select your timezone...</option>
                        {TIMEZONE_OPTIONS.map((tz) => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label}
                          </option>
                        ))}
                      </select>
                      <small className="field-hint">
                        Please choose your timezone for accurate scheduling.
                      </small>
                    </div>

                    {!arrivedByLink && !storedCode && (
                      <div className="form-group">
                        <label htmlFor="signupCode">Referral or promo code</label>
                        <input
                          id="signupCode"
                          name="signupCode"
                          type="text"
                          value={typedCode}
                          onChange={(e) => setTypedCode(e.target.value.toUpperCase())}
                          placeholder="Leave blank if you have none"
                          className="form-input"
                        />
                        <small className="field-hint">
                          A promotion code, or the code the person who referred you gave you.
                        </small>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Business Information */}
                {currentStep === 2 && (
                  <div className="form-step">
                    <div className="form-group">
                      <label htmlFor="businessName">Business Name *</label>
                      <input
                        type="text"
                        id="businessName"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleInputChange}
                        className={errors.businessName ? "error" : ""}
                        placeholder="Elegant Cuts Salon"
                      />
                      {errors.businessName && (
                        <span className="error-message">
                          {errors.businessName}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Business Type(s) *</label>
                      {loadingTypes ? (
                        <div
                          style={{
                            padding: "20px",
                            textAlign: "center",
                            color: "#6b7280",
                          }}
                        >
                          Loading business categories...
                        </div>
                      ) : (
                        <div className="business-type-grid">
                          {businessTypes.map((type) => (
                            <button
                              key={type.value}
                              type="button"
                              className={`business-type-button ${formData.businessType.includes(type.value) ? "selected" : ""}`}
                              onClick={() =>
                                handleBusinessTypeChange(type.value)
                              }
                            >
                              <span style={{ fontSize: "1.25rem" }}>
                                {type.emoji}
                              </span>
                              <span style={{ textAlign: "center" }}>
                                {type.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                      {errors.businessType && (
                        <span className="error-message">
                          {errors.businessType}
                        </span>
                      )}
                      <small className="field-hint">
                        Select all that apply
                      </small>
                    </div>
                  </div>
                )}

                {/* Step 3: Business Address */}
                {currentStep === 3 && (
                  <div className="form-step">
                    <div className="form-group">
                      <label htmlFor="locationMode">First Location Mode *</label>
                      <select
                        id="locationMode"
                        name="locationMode"
                        value={formData.locationMode}
                        onChange={handleInputChange}
                        className={`classy-input ${errors.locationMode ? "error" : ""}`}
                        required
                        style={{ width: '100%' }}
                      >
                        <option value="">Select location mode</option>
                        <option value="commission_only">Commission only</option>
                        <option value="hybrid">Hybrid</option>
                        <option value="booth_rental_only">Booth rental only</option>
                      </select>
                      {errors.locationMode && (
                        <span className="error-message">{errors.locationMode}</span>
                      )}
                      {/* U4: explain commission vs booth rental for the location mode */}
                      <div
                        style={{
                          marginTop: "8px",
                          padding: "10px 12px",
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          borderRadius: "8px",
                          fontSize: "13px",
                          lineHeight: 1.55,
                          color: "#1e3a5f",
                        }}
                      >
                        <strong>Commission vs Booth Rental — what's the difference?</strong>
                        <ul style={{ margin: "6px 0 0 0", paddingLeft: "18px" }}>
                          <li>
                            <strong>Commission only</strong> — you run this location
                            and sell your own products/services; OGESTYLE takes a
                            small platform fee on each sale.
                          </li>
                          <li>
                            <strong>Booth rental only</strong> — independent
                            professionals (booth renters) pay you rent for their
                            space; their sales are theirs and you don't earn
                            commission on them.
                          </li>
                          <li>
                            <strong>Hybrid</strong> — both: you earn commission on
                            your own sales AND collect rent from booth renters.
                          </li>
                        </ul>
                      </div>
                      <small className="field-hint">
                        This sets how the first location will operate from day one.
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="street">Street Address *</label>
                        <AddressAutocompleteInput
                          value={formData.street}
                          onChange={(val) =>
                            setFormData((prev) => ({ ...prev, street: val }))
                          }
                          onAddressSelect={({ street, city, state, zip, lat, lng }) => {
                            setFormData((prev) => ({
                              ...prev,
                              street: street || prev.street,
                              ...(city && { city }),
                              ...(state && { state }),
                              ...(zip && { zip }),
                              lat: lat || '',
                              lng: lng || ''
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              street: "",
                              city: "",
                              state: "",
                              zip: "",
                              lat: ""
                            }));
                          }}
                          placeholder="123 W Madison St"
                          required
                          inputStyle={{
                            width: "100%",
                            padding: "10px",
                            border: errors.street ? "1px solid #ef4444" : "1px solid #d1d5db",
                            borderRadius: "4px",
                            fontSize: "14px",
                          }}
                        />
                      {errors.street && (
                        <span className="error-message">{errors.street}</span>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="city">City *</label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className={errors.city ? "error" : ""}
                          placeholder="Chicago"
                        />
                        {errors.city && (
                          <span className="error-message">{errors.city}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="state">State *</label>
                        <input
                          type="text"
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          className={errors.state ? "error" : ""}
                          placeholder="IL"
                          maxLength="2"
                        />
                        {errors.state && (
                          <span className="error-message">{errors.state}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="zip">ZIP Code *</label>
                      <input
                        type="text"
                        id="zip"
                        name="zip"
                        value={formData.zip}
                        onChange={handleInputChange}
                        className={errors.zip ? "error" : ""}
                        placeholder="10001"
                        maxLength="10"
                      />
                      {errors.zip && (
                        <span className="error-message">{errors.zip}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 4: Business Contact Details */}
                {currentStep === 4 && (
                  <div className="form-step">
                    <p className="optional-section-note">
                      These are the public-facing business contact details.
                    </p>

                    <div className="form-group">
                      <label htmlFor="businessPhone">Business Phone (optional)</label>
                      <input
                        type="tel"
                        id="businessPhone"
                        name="businessPhone"
                        value={formData.businessPhone}
                        onChange={handleInputChange}
                        className={errors.businessPhone ? "error" : ""}
                        placeholder="(555) 987-6543"
                      />
                      {errors.businessPhone && (
                        <span className="error-message">{errors.businessPhone}</span>
                      )}
                      <small className="field-hint">
                        Used as your business contact number.
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="businessEmail">Business Email (optional)</label>
                      <input
                        type="email"
                        id="businessEmail"
                        name="businessEmail"
                        value={formData.businessEmail}
                        onChange={handleInputChange}
                        className={errors.businessEmail ? "error" : ""}
                        placeholder="contact@yourbusiness.com"
                      />
                      {errors.businessEmail && (
                        <span className="error-message">{errors.businessEmail}</span>
                      )}
                      <small className="field-hint">
                        Used as your business contact email.
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="website">Website</label>
                      <input
                        type="url"
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        placeholder="https://www.yourbusiness.com"
                      />
                    </div>

                    <div className="form-group">
                      <label>Social Media</label>
                      <div className="social-inputs">
                        <div className="social-input-group">
                          <span className="social-icon">📘</span>
                          <input
                            type="text"
                            name="facebook"
                            value={formData.facebook}
                            onChange={handleInputChange}
                            placeholder="Facebook username"
                          />
                        </div>
                        <div className="social-input-group">
                          <span className="social-icon">📷</span>
                          <input
                            type="text"
                            name="instagram"
                            value={formData.instagram}
                            onChange={handleInputChange}
                            placeholder="Instagram username"
                          />
                        </div>
                        <div className="social-input-group">
                          <span className="social-icon">🐦</span>
                          <input
                            type="text"
                            name="twitter"
                            value={formData.twitter}
                            onChange={handleInputChange}
                            placeholder="Twitter username"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error message */}
                {errors.submit && (
                  <div className="error-alert">{errors.submit}</div>
                )}

                {/* Navigation Buttons */}
                <div className="form-navigation">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleBack}
                      disabled={isSubmitting}
                    >
                      ← Back
                    </button>
                  )}
                  {currentStep < totalSteps ? (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleNext}
                    >
                      Next →
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-primary btn-submit"
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                    >
                      {isSubmitting
                        ? "Creating Account..."
                        : "Complete Registration"}
                    </button>
                  )}
                </div>
              </form>

              <div className="registration-footer">
                <p>
                  Already have an account? <a href="/login">Sign in here</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomerFooter />
    </>
  );
};

export default CompleteOwnerRegistration;
